import {type InfiniteData, infiniteQueryOptions, keepPreviousData} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {DEFAULT_PROJECT_SORT, type ProjectSortValue} from '../components/projects/project-sort-options'
import type {ProjectsResponse} from '../components/projects/types'

const PROJECTS_QUERY_KEY = 'repo-projects'

/**
 * Partial queryKey shared by every `projectsInfiniteQueryOptions` entry for a single
 * owner/repo, regardless of state/sort/template variant. Cache-reader and mutation
 * hooks use it for `getQueriesData` / `invalidateQueries` partial matches, deriving the
 * key from this helper rather than hand-building it so it can't drift from the factory.
 */
export function projectsQueryKey(owner: string, repo: string) {
  return [PROJECTS_QUERY_KEY, owner, repo] as const
}

interface ProjectsQueryArgs {
  owner: string
  repo: string
  state: 'open' | 'closed'
  query?: string
  sort?: ProjectSortValue
  isTemplateView?: boolean
}

interface PageParam {
  cursor: string | null
  sortQueryCursor: string | null
}

const INITIAL_PAGE_PARAM: PageParam = {cursor: null, sortQueryCursor: null}

export function projectsInfiniteQueryOptions({
  owner,
  repo,
  state,
  query,
  sort = DEFAULT_PROJECT_SORT,
  isTemplateView = false,
}: ProjectsQueryArgs) {
  const trimmedQuery = query?.trim() ?? ''

  return infiniteQueryOptions<ProjectsResponse, Error, InfiniteData<ProjectsResponse>, readonly unknown[], PageParam>({
    queryKey: [...projectsQueryKey(owner, repo), state, trimmedQuery, sort, isTemplateView],
    initialPageParam: INITIAL_PAGE_PARAM,
    placeholderData: keepPreviousData,
    queryFn: async ({pageParam}) => {
      const baseQuery = state === 'closed' ? 'is:closed' : 'is:open'
      // The projects-list endpoint expects sort and template state as qualifiers inside
      // the `query` string. Omit defaults so URLs and requests stay clean.
      const sortQualifier = sort !== DEFAULT_PROJECT_SORT ? `sort:${sort}` : ''
      const templateQualifier = isTemplateView ? 'is:template' : ''
      const fullQuery = [baseQuery, trimmedQuery, sortQualifier, templateQualifier].filter(Boolean).join(' ')
      const params = new URLSearchParams({query: fullQuery})
      if (pageParam.cursor) {
        params.set('cursor', pageParam.cursor)
      }
      if (pageParam.sortQueryCursor) {
        params.set('sort_query_cursor', pageParam.sortQueryCursor)
      }
      const response = await verifiedFetchJSON(`/${owner}/${repo}/projects?${params.toString()}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`)
      }
      return (await response.json()) as ProjectsResponse
    },
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? {cursor: lastPage.cursor, sortQueryCursor: lastPage.sortQueryCursor} : undefined,
  })
}
